import Btn from './shared/Btn';
import { STEPS, findStepIndexByField, countErrorsForStep } from './STEPS';

/**
 * @param {{
 *   result: { valid: boolean, errors: any[], warnings: any[] }|null,
 *   validationError?: { detail?: string }|null,
 *   errors: Array<{field?: string, severity?: string, message?: string, code?: string}>,
 *   validating: boolean,
 *   editable: boolean,
 *   onRunValidation: () => void,
 *   onPreview: () => void,
 *   onSubmit: () => void,
 *   onJumpToStep: (idx: number) => void,
 *   draftStatus?: string,
 *   onMarkAsSubmitted?: (() => void)|null,
 * }} props
 */
export default function ValidationPanel({
  result, validationError, errors, validating, editable,
  onRunValidation, onPreview, onSubmit, onJumpToStep,
  draftStatus, onMarkAsSubmitted = null,
}) {
  const errorCount = result?.errors?.length || 0;
  const warningCount = result?.warnings?.length || 0;
  // Distinguish "validated and clean" from "never validated yet". Until the
  // user (or Continue) runs validation, we have no result and the green dots
  // / "Ready" chip are misleading — show a neutral "Not validated" state.
  const hasResult = !!result;
  const readinessLabel = !hasResult
    ? 'Not validated'
    : (errorCount > 0 ? `${errorCount} issue${errorCount > 1 ? 's' : ''}`
      : (warningCount > 0 ? `${warningCount} warning${warningCount > 1 ? 's' : ''}`
        : 'Ready'));
  const readinessChipBg = !hasResult ? 'var(--ab-bg-alt)'
    : (errorCount ? 'var(--ab-denied-soft)'
      : (warningCount ? 'var(--ab-pending-soft)' : 'var(--ab-approved-soft)'));
  const readinessChipFg = !hasResult ? 'var(--ab-fg-subtle)'
    : (errorCount ? 'var(--ab-denied)'
      : (warningCount ? 'var(--ab-pending)' : 'var(--ab-approved)'));

  return (
    <aside style={{
      width: 320, position: 'sticky', top: 0, alignSelf: 'flex-start',
      display: 'flex', flexDirection: 'column', gap: 16,
      padding: 20,
      borderLeft: '1px solid var(--ab-border-hair)',
      background: 'var(--ab-bg)',
    }}>
      {/* Card 1: Readiness */}
      <div style={{ border: 'var(--ab-hairline)', borderRadius: 'var(--ab-radius-md)', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 'var(--ab-weight-semi)' }}>Readiness</div>
          <span style={{
            fontSize: 11, fontWeight: 'var(--ab-weight-bold)',
            letterSpacing: 'var(--ab-tracking-label)', textTransform: 'uppercase',
            padding: '2px 8px', borderRadius: 'var(--ab-radius-pill)',
            background: readinessChipBg,
            color: readinessChipFg,
          }}>{readinessLabel}</span>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {STEPS.map((step, i) => {
            if (step.errorPrefixes.length === 0) return null; // skip docs/review (they own no errors)
            const stepErrors = (errors || []).filter((e) =>
              step.errorPrefixes.some((p) => {
                const f = String(e.field || '').replace(/\[(\d+)\]/g, '.$1');
                return f === p || f.startsWith(p + '.');
              })
            );
            const hasErr = stepErrors.some((e) => e.severity !== 'warning');
            const hasWarn = stepErrors.some((e) => e.severity === 'warning');
            // Until validation has run, we don't know which steps are clean —
            // show a neutral gray dot rather than a falsely-confident green.
            const dot = !hasResult
              ? 'var(--ab-border-soft)'
              : (hasErr ? 'var(--ab-denied)' : (hasWarn ? 'var(--ab-pending)' : 'var(--ab-approved)'));
            return (
              <li key={step.key}>
                <button
                  type="button"
                  onClick={() => onJumpToStep(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '4px 0', fontSize: 12,
                    color: hasErr ? 'var(--ab-denied)' : 'var(--ab-fg)',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
                  {step.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Card 2: Actions */}
      <div style={{ border: 'var(--ab-hairline)', borderRadius: 'var(--ab-radius-md)', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)', marginBottom: 4 }}>
          {editable ? 'Actions' : 'Submitted'}
        </div>
        {editable && (
          <Btn kind="secondary" icon="check-circle" onClick={onRunValidation} disabled={validating}>
            {validating ? 'Running…' : 'Run Validation'}
          </Btn>
        )}
        <Btn kind="secondary" icon="eye" onClick={onPreview}>Preview CMS-1500</Btn>
        {editable && (
          <Btn kind="primary" icon="send" onClick={onSubmit} aria-disabled={errorCount > 0}>
            Submit Claim
          </Btn>
        )}
        {editable && draftStatus === 'validated' && onMarkAsSubmitted && (
          <Btn kind="secondary" icon="check-circle" onClick={onMarkAsSubmitted}>
            Mark as Submitted
          </Btn>
        )}
      </div>

      {/* Card 3: Validation request failure */}
      {validationError && (
        <div style={{ border: 'var(--ab-hairline)', borderRadius: 'var(--ab-radius-md)', padding: 16, background: 'var(--ab-denied-soft)' }}>
          <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)', marginBottom: 6, color: 'var(--ab-denied)' }}>
            Validation unavailable
          </div>
          <div style={{ fontSize: 12, color: 'var(--ab-denied)' }}>
            {validationError.detail || 'Unable to run validation.'}
          </div>
        </div>
      )}

      {/* Card 4: Errors / Warnings */}
      {result && (errorCount > 0 || warningCount > 0) && (
        <div style={{ border: 'var(--ab-hairline)', borderRadius: 'var(--ab-radius-md)', padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)', marginBottom: 8 }}>Issues</div>
          {[...result.errors, ...result.warnings].map((/** @type {any} */ e, i) => (
            <button
              key={`${e.field}-${i}`}
              type="button"
              onClick={() => {
                const stepIdx = findStepIndexByField(e.field);
                if (stepIdx >= 0) onJumpToStep(stepIdx);
                const el = /** @type {any} */ (document.querySelector(`[data-field="${e.field}"]`));
                if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                paddingLeft: 8, marginBottom: 6,
                borderLeft: `3px solid ${e.severity === 'warning' ? 'var(--ab-pending)' : 'var(--ab-denied)'}`,
                background: 'transparent', cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 'var(--ab-weight-semi)' }} data-field={e.field} data-code={e.code}>
                {e.field}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>{e.message}</div>
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}
